import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Pagination, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Section, Eyebrow, Card } from "../components/ui";
import { tokens } from "../theme";
import { errorMessage, getRxCatalog, getRxOrders, getVitaRelayStatus, placeRxOrder, referTelehealthIntake, startTelehealthIntake } from "../services/veysoApi";

const PAGE_SIZE = 24;
const ORDER_STATUSES = ["awaiting_payment", "submitted", "processing", "assigned", "ready_to_ship", "shipped", "delivered", "cancelled", "returned"];

const money = (cents, currency = "USD") =>
  cents == null ? "-" : new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const newFormId = () =>
  (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `form-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const EMPTY_FORM = { firstName: "", lastName: "", dob: "", line1: "", line2: "", city: "", state: "", postalCode: "" };

/** Admin page: browse the VitaRelay Rx catalog, place an order, and see orders. */
export default function VitaRelay() {
  const [tab, setTab] = useState("catalog");
  const [status, setStatus] = useState(null);
  const [tray, setTray] = useState([]); // [{ id, name, quantity }]
  const [orderOpen, setOrderOpen] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [intakeProduct, setIntakeProduct] = useState(null); // product whose telehealth intake is open
  const [ordersKey, setOrdersKey] = useState(0); // bump to reload the orders tab after an order

  useEffect(() => {
    getVitaRelayStatus().then(setStatus).catch(() => setStatus({ configured: false, sandbox: false, failed: true }));
  }, []);

  const addToTray = (p) =>
    setTray((t) => (t.some((x) => x.id === p.id) ? t.map((x) => (x.id === p.id ? { ...x, quantity: x.quantity + 1 } : x)) : [...t, { id: p.id, name: p.name, quantity: 1 }]));

  return (
    <Section tone="cream" sx={{ pt: { xs: 14, md: 18 } }}>
      <Eyebrow>Admin</Eyebrow>
      <Typography variant="h3" sx={{ mb: 1 }}>Rx catalog and orders</Typography>
      <Typography sx={{ color: "text.secondary", mb: 2 }}>Products and orders from VitaRelay. Prices are set by VitaRelay when the order is placed.</Typography>

      {status && status.failed && <Alert severity="error" sx={{ mb: 2 }}>Could not check the VitaRelay connection. Make sure you are signed in as an Admin.</Alert>}
      {status && !status.failed && !status.configured && <Alert severity="warning" sx={{ mb: 2 }}>VitaRelay is not set up on the server yet (VITARELAY_API_KEY is missing).</Alert>}
      {status && status.configured && status.sandbox && <Alert severity="info" sx={{ mb: 2 }}>Sandbox mode: orders here are simulated and no one is charged or shipped to.</Alert>}
      {placed && (
        <Alert severity="success" onClose={() => setPlaced(null)} sx={{ mb: 2 }}>
          Order {placed.orderNumber || placed.id} placed ({placed.status}){placed.totalCents != null ? `, total ${money(placed.totalCents)}` : ""}.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="catalog" label="Catalog" />
        <Tab value="orders" label="Orders" />
      </Tabs>

      {tab === "catalog" && <CatalogTab onAdd={addToTray} onIntake={setIntakeProduct} />}
      {tab === "orders" && <OrdersTab reloadKey={ordersKey} />}

      {tray.length > 0 && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1200, background: tokens.forestDark, color: "#fff", py: 1.5, px: { xs: 2, md: 4 }, display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between" }}>
          <Typography>{tray.reduce((n, x) => n + x.quantity, 0)} item(s) in this order</Typography>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" onClick={() => setTray([])}>Clear</Button>
            <Button variant="contained" onClick={() => setOrderOpen(true)}>Review and place order</Button>
          </Stack>
        </Box>
      )}

      {intakeProduct && <IntakeDialog product={intakeProduct} onClose={() => setIntakeProduct(null)} />}

      {orderOpen && (
        <OrderDialog
          tray={tray}
          setTray={setTray}
          onClose={() => setOrderOpen(false)}
          onPlaced={(result) => {
            setPlaced(result);
            setTray([]);
            setOrderOpen(false);
            setOrdersKey((k) => k + 1);
            setTab("orders");
          }}
        />
      )}
    </Section>
  );
}

function CatalogTab({ onAdd, onIntake }) {
  const [source, setSource] = useState("rx"); // rx = prescription (503A), research = research supplies (with prices)
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getRxCatalog({ source, page, limit: PAGE_SIZE, q: query || undefined })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, "Could not load the catalog.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [source, page, query]);

  const submit = (e) => { e.preventDefault(); setPage(1); setQuery(q.trim()); };
  const switchSource = (_, v) => { if (v) { setSource(v); setPage(1); setData(null); } };
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Box>
      <ToggleButtonGroup exclusive size="small" value={source} onChange={switchSource} sx={{ mb: 2 }}>
        <ToggleButton value="rx">Prescription (503A)</ToggleButton>
        <ToggleButton value="research">Research supplies</ToggleButton>
      </ToggleButtonGroup>
      <Box component="form" onSubmit={submit} sx={{ display: "flex", gap: 1, mb: 3, maxWidth: 480 }}>
        <TextField size="small" fullWidth placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button type="submit" variant="contained">Search</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>}
      {!loading && data && (
        <>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>{data.total} products</Typography>
          <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
            {data.items.map((p) => (
              <Card key={p.id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {p.imageUrls && p.imageUrls[0] && (
                  <Box component="img" src={p.imageUrls[0]} alt={p.name} loading="lazy" sx={{ width: "100%", height: 160, objectFit: "contain", background: "#fff", borderRadius: "12px" }} />
                )}
                <Typography variant="overline" sx={{ color: tokens.forest }}>{p.categoryLabel || p.category}</Typography>
                <Typography variant="h6">{p.name}</Typography>
                {p.description && (
                  <Typography variant="body2" sx={{ color: "text.secondary", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description}</Typography>
                )}
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {p.dosageForm && <Chip size="small" label={p.dosageForm} />}
                  {p.volume && <Chip size="small" label={p.volume} />}
                  {(p.strengths || []).map((s) => <Chip key={s} size="small" variant="outlined" label={s} />)}
                </Stack>
                <Box sx={{ flex: 1 }} />
                {p.priceCents != null && <Typography sx={{ fontWeight: 700 }}>{money(p.priceCents)}</Typography>}
                {source === "rx" && <Button variant="contained" onClick={() => onIntake(p)}>Start telehealth intake</Button>}
                <Button variant={source === "rx" ? "text" : "outlined"} onClick={() => onAdd(p)}>Add to order</Button>
              </Card>
            ))}
          </Box>
          {data.items.length === 0 && <Typography sx={{ py: 4 }}>No products match that search.</Typography>}
          {pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 8 }}>
              <Pagination count={pages} page={page} onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function OrdersTab({ reloadKey }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback((next) => {
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    getRxOrders({ limit: 25, cursor: next || undefined, status: statusFilter || undefined })
      .then((d) => {
        if (mine !== seq.current) return;
        setRows((r) => (next ? [...r, ...d.items] : d.items));
        setCursor(d.nextCursor);
      })
      .catch((e) => { if (mine === seq.current) setError(errorMessage(e, "Could not load orders.")); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [statusFilter]);

  useEffect(() => { load(null); }, [load, reloadKey]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {ORDER_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>)}
        </TextField>
        <Button onClick={() => load(null)}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ overflowX: "auto", background: "#fff", borderRadius: "16px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell><TableCell>Status</TableCell><TableCell>Payment</TableCell>
              <TableCell align="right">Total</TableCell><TableCell>Tracking</TableCell><TableCell>Placed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id}>
                <TableCell>{o.orderNumber || o.id}</TableCell>
                <TableCell><Chip size="small" label={(o.status || "").replace(/_/g, " ")} /></TableCell>
                <TableCell>{o.paymentStatus || "-"}</TableCell>
                <TableCell align="right">{money(o.totalCents, o.currency || "USD")}</TableCell>
                <TableCell>
                  {o.trackingNumber
                    ? (o.trackingUrl ? <a href={o.trackingUrl} target="_blank" rel="noreferrer">{o.trackingCarrier} {o.trackingNumber}</a> : `${o.trackingCarrier || ""} ${o.trackingNumber}`)
                    : "-"}
                </TableCell>
                <TableCell>{when(o.createdAt)}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>No orders yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {loading && <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>}
      {!loading && cursor && <Box sx={{ textAlign: "center", mt: 2 }}><Button onClick={() => load(cursor)}>Load more</Button></Box>}
    </Box>
  );
}

function OrderDialog({ tray, setTray, onClose, onPlaced }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // One id per dialog: a double click or retry cannot create two orders.
  const formId = useRef(newFormId());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setQty = (id, qty) => setTray((t) => t.map((x) => (x.id === id ? { ...x, quantity: Math.max(1, Math.min(99, Number(qty) || 1)) } : x)));
  const remove = (id) => setTray((t) => t.filter((x) => x.id !== id));

  const ready = form.firstName && form.lastName && /^\d{4}-\d{2}-\d{2}$/.test(form.dob) && form.line1 && form.city && /^[A-Za-z]{2}$/.test(form.state) && form.postalCode && tray.length > 0;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const result = await placeRxOrder({
        clientOrderId: `veyso-${formId.current}`,
        patient: { firstName: form.firstName.trim(), lastName: form.lastName.trim(), dob: form.dob },
        shipping: { line1: form.line1.trim(), line2: form.line2.trim() || null, city: form.city.trim(), state: form.state.trim(), postalCode: form.postalCode.trim() },
        items: tray.map((x) => ({ productId: x.id, quantity: x.quantity })),
      });
      onPlaced(result);
    } catch (e) {
      setError(errorMessage(e, "The order could not be placed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Review and place order</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Items</Typography>
        {tray.map((x) => (
          <Stack key={x.id} direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
            <Typography sx={{ flex: 1 }}>{x.name}</Typography>
            <TextField size="small" type="number" value={x.quantity} onChange={(e) => setQty(x.id, e.target.value)} sx={{ width: 80 }} slotProps={{ htmlInput: { min: 1, max: 99 } }} />
            <IconButton aria-label={`Remove ${x.name}`} onClick={() => remove(x.id)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        ))}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Patient</Typography>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "1fr 1fr" }}>
          <TextField size="small" label="First name" value={form.firstName} onChange={set("firstName")} />
          <TextField size="small" label="Last name" value={form.lastName} onChange={set("lastName")} />
          <TextField size="small" label="Date of birth" placeholder="YYYY-MM-DD" value={form.dob} onChange={set("dob")} sx={{ gridColumn: "1 / -1" }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Ship to</Typography>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "1fr 1fr" }}>
          <TextField size="small" label="Address line 1" value={form.line1} onChange={set("line1")} sx={{ gridColumn: "1 / -1" }} />
          <TextField size="small" label="Address line 2 (optional)" value={form.line2} onChange={set("line2")} sx={{ gridColumn: "1 / -1" }} />
          <TextField size="small" label="City" value={form.city} onChange={set("city")} />
          <TextField size="small" label="State (2 letters)" value={form.state} onChange={set("state")} slotProps={{ htmlInput: { maxLength: 2 } }} />
          <TextField size="small" label="ZIP code" value={form.postalCode} onChange={set("postalCode")} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!ready || submitting}>{submitting ? "Placing..." : "Place order"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Telehealth intake: patient details -> location -> "check your email or phone" -> refer to clinician.
// The server checks every field too; this just tells the person sooner.
// ---------------------------------------------------------------------------------------------

const EMPTY_INTAKE = { firstName: "", lastName: "", dob: "", channel: "email", email: "", phone: "", address1: "", address2: "", city: "", state: "", postalCode: "" };
const STEP1_FIELDS = ["firstName", "lastName", "dob", "channel", "email", "phone"];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Same rules as the server's TelehealthIntakeValidator. Returns { field: message }. */
function checkIntake(f) {
  const e = {};
  if (!f.firstName.trim()) e.firstName = "Enter a first name.";
  if (!f.lastName.trim()) e.lastName = "Enter a last name.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.dob)) {
    e.dob = "Enter the date of birth.";
  } else {
    const [y, m, d] = f.dob.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const real = dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
    const now = new Date();
    const age = now.getFullYear() - y - (now < new Date(now.getFullYear(), m - 1, d) ? 1 : 0);
    if (!real) e.dob = "That is not a real date.";
    else if (dt > now || age > 120) e.dob = "That date of birth does not look right. Check the year.";
    else if (age < 18) e.dob = "The patient must be 18 or older.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address.";
  let digits = f.phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10 || digits[0] === "0" || digits[0] === "1") e.phone = "Enter a 10-digit US mobile number.";
  if (!f.address1.trim()) e.address1 = "Enter the street address.";
  if (!f.city.trim()) e.city = "Enter the city.";
  if (!/^[A-Za-z]{2}$/.test(f.state.trim())) e.state = "Use the 2-letter state, for example MN.";
  if (!/^\d{5}(-\d{4})?$/.test(f.postalCode.trim())) e.postalCode = "Enter a 5-digit ZIP code.";
  return e;
}

function IntakeDialog({ product, onClose }) {
  const [step, setStep] = useState(1); // 1 patient, 2 location, 3 link sent, 4 referred
  const [form, setForm] = useState(EMPTY_INTAKE);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(null); // { patientId, referralToken, channel, message, mock } (kept in memory only)
  const [referred, setReferred] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => (x[k] ? { ...x, [k]: undefined } : x));
  };
  const field = (k, label, extra = {}) => (
    <TextField size="small" label={label} value={form[k]} onChange={set(k)} error={Boolean(errors[k])} helperText={errors[k] || " "} {...extra} />
  );

  const next = () => {
    const all = checkIntake(form);
    const mine = Object.fromEntries(Object.entries(all).filter(([k]) => STEP1_FIELDS.includes(k)));
    setErrors(mine);
    if (Object.keys(mine).length === 0) setStep(2);
  };

  const send = async () => {
    const all = checkIntake(form);
    setErrors(all);
    if (Object.keys(all).length > 0) {
      if (Object.keys(all).some((k) => STEP1_FIELDS.includes(k))) setStep(1);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await startTelehealthIntake({ ...form, productName: product.name });
      setStarted(result);
      setStep(3);
    } catch (e) {
      const fe = e?.response?.data?.fieldErrors;
      if (fe) {
        setErrors(fe);
        if (Object.keys(fe).some((k) => STEP1_FIELDS.includes(k))) setStep(1);
        setError("Please fix the highlighted fields.");
      } else {
        setError(errorMessage(e, "Could not start the intake."));
      }
    } finally {
      setBusy(false);
    }
  };

  const refer = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await referTelehealthIntake({
        patientId: started.patientId,
        referralToken: started.referralToken,
        productName: product.name,
      });
      setReferred(result);
      setStep(4);
    } catch (e) {
      setError(errorMessage(e, "Could not send the referral."));
    } finally {
      setBusy(false);
    }
  };

  const how = form.channel === "sms" ? "text message" : "email";
  const titles = { 1: "Tell us who the patient is", 2: "Where is the patient located?", 3: `Check the patient's ${how}`, 4: "Sent to a clinician" };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="overline" sx={{ display: "block", color: tokens.forest }}>
          Telehealth intake{step <= 2 ? ` - step ${step} of 2` : ""}
        </Typography>
        {titles[step]}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>Evaluation requested for: <b>{product.name}</b></Typography>

        {step === 1 && (
          <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "1fr 1fr" }}>
            {field("firstName", "First name")}
            {field("lastName", "Last name")}
            {field("dob", "Date of birth", { type: "date", slotProps: { inputLabel: { shrink: true }, htmlInput: { min: "1900-01-01", max: todayIso() } } })}
            {field("channel", "Send intake by", { select: true, children: [<MenuItem key="e" value="email">Email</MenuItem>, <MenuItem key="s" value="sms">Text message</MenuItem>] })}
            {field("email", "Email", { type: "email" })}
            {field("phone", "Mobile phone", { placeholder: "612 555 0142" })}
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "1fr 1fr" }}>
            {field("address1", "Street address", { sx: { gridColumn: "1 / -1" } })}
            {field("address2", "Address line 2 (optional)", { sx: { gridColumn: "1 / -1" } })}
            {field("city", "City")}
            {field("state", "State (2 letters)", { slotProps: { htmlInput: { maxLength: 2 } } })}
            {field("postalCode", "ZIP code")}
          </Box>
        )}

        {step === 3 && started && (
          <Box>
            {started.mock && <Alert severity="info" sx={{ mb: 2 }}>Test mode: nothing was really sent, and no one has been contacted.</Alert>}
            {started.suppressed && <Alert severity="warning" sx={{ mb: 2 }}>The link could not be sent to that {how}. Try the other way, or check the address.</Alert>}
            <Typography sx={{ mb: 2 }}>{started.message}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              The patient completes the secure medical intake from that link. A licensed clinician will then review it and decide whether a prescription is medically appropriate.
              When they say they are done, press the button below.
            </Typography>
          </Box>
        )}

        {step === 4 && referred && (
          <Box>
            {referred.mock && <Alert severity="info" sx={{ mb: 2 }}>Test mode: nothing was really sent, and no one has been contacted.</Alert>}
            <Typography sx={{ mb: 1 }}>{referred.message}</Typography>
            {referred.pendingActivation && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                The patient is not fully set up yet. The clinician's decision comes after the intake is complete. If it is approved, the order can be placed.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 1 && (<><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={next}>Continue</Button></>)}
        {step === 2 && (<><Button onClick={() => setStep(1)} disabled={busy}>Back</Button><Button variant="contained" onClick={send} disabled={busy}>{busy ? "Sending..." : "Send secure intake"}</Button></>)}
        {step === 3 && (<><Button onClick={onClose} disabled={busy}>Close</Button><Button variant="contained" onClick={refer} disabled={busy}>{busy ? "Sending..." : "I've completed the intake"}</Button></>)}
        {step === 4 && <Button variant="contained" onClick={onClose}>Done</Button>}
      </DialogActions>
    </Dialog>
  );
}
